/**
 * Aline — assistente que ensina a usar o Gerencie Odonto (onde fica cada funcionalidade e como usar).
 * Fica disponível em todas as telas do sistema (montada no Layout). Não dá orientação tributária
 * e não acessa dados da clínica: o backend responde só com base no guia do sistema.
 *
 * A resposta pode trazer botões no formato [[Texto|/rota]]; só rotas da lista abaixo abrem (nada externo).
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Box, Paper, Typography, TextField, IconButton, Avatar, Fab, CircularProgress, Chip, useMediaQuery,
} from '@mui/material';
import { Send as SendIcon, Close as CloseIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import chatService from '../services/chatService';
import { useAuth } from '../context/AuthContext';

const ALINE_FOTO = '/assets/aline_foto.png';
const ROXO = 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)';
const CHAVE_SESSAO = 'aline_conversa_v1';

// mesmas rotas que o backend (services/ajudaSistema.js) pode oferecer como botão
const ROTAS_PERMITIDAS = new Set([
  '/dashboard', '/faturamento', '/maquinas-cartao', '/despesas', '/plano-contas', '/clinica-dashboard',
  '/pacientes', '/procedimentos', '/agenda', '/termos', '/lancamentos', '/relatorios', '/analise',
  '/gerenciar-usuarios', '/perfil', '/usuario/configuracao', '/usuario/assinatura', '/sugestoes',
]);

const SUGESTOES_DONO = [
  'Como lanço um faturamento?',
  'Onde cadastro as taxas da máquina de cartão?',
  'Como agendo uma consulta?',
  'Como envio um termo para o paciente assinar?',
  'Como registro dois procedimentos no mesmo dente?',
  'Como cadastro uma secretária?',
];
const SUGESTOES_EQUIPE = [
  'Como agendo uma consulta?',
  'Como cadastro um paciente?',
  'Como envio um termo para o paciente assinar?',
  'Como registro procedimentos no odontograma?',
];

// separa os botões [[Texto|/rota]] do texto e valida as rotas
function extrairBotoes(texto) {
  const botoes = [];
  const limpo = String(texto || '').replace(/\[\[([^\]|]+)\|([^\]]+)\]\]/g, (_, rotulo, rota) => {
    const caminho = rota.trim();
    if (ROTAS_PERMITIDAS.has(caminho) && botoes.length < 2) botoes.push({ rotulo: rotulo.trim(), caminho });
    return '';
  }).trim();
  return { texto: limpo, botoes };
}

// **negrito** simples
function TextoFormatado({ texto }) {
  const partes = String(texto).split(/(\*\*[^*]+\*\*)/g);
  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {partes.map((p, i) => (p.startsWith('**') && p.endsWith('**')
        ? <strong key={i}>{p.slice(2, -2)}</strong>
        : <React.Fragment key={i}>{p}</React.Fragment>))}
    </Typography>
  );
}

export default function AssistenteAline() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const celular = useMediaQuery(theme.breakpoints.down('sm'));
  const [aberto, setAberto] = useState(false);
  const [mensagens, setMensagens] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(CHAVE_SESSAO) || '[]'); } catch { return []; }
  });
  const [texto, setTexto] = useState('');
  const [carregando, setCarregando] = useState(false);
  const fimRef = useRef(null);
  const abriuPrimeiroAcesso = useRef(false);

  const equipe = user?.role === 'secretaria' || user?.role === 'dentista_parceiro';
  const sugestoes = equipe ? SUGESTOES_EQUIPE : SUGESTOES_DONO;
  const primeiroNome = useMemo(() => String(user?.nome || '').replace(/^(dr\.?a?|dra\.?)\s+/i, '').split(' ')[0], [user?.nome]);

  // primeiro acesso: abre sozinha uma vez (como acontecia antes no Dashboard)
  useEffect(() => {
    if (user?.primeiroAcesso && !abriuPrimeiroAcesso.current) {
      abriuPrimeiroAcesso.current = true;
      setAberto(true);
    }
  }, [user?.primeiroAcesso]);

  useEffect(() => { fimRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensagens, carregando, aberto]);
  useEffect(() => {
    try { sessionStorage.setItem(CHAVE_SESSAO, JSON.stringify(mensagens.slice(-30))); } catch { /* sem armazenamento */ }
  }, [mensagens]);

  const fechar = () => {
    setAberto(false);
    if (user?.primeiroAcesso) chatService.markNotFirstAccess().catch(() => {});
  };

  const enviar = async (valor) => {
    const pergunta = String(valor ?? texto).trim();
    if (!pergunta || carregando) return;
    const historico = mensagens.map((m) => ({ sender: m.sender, text: m.texto }));
    setMensagens((prev) => [...prev, { id: Date.now(), sender: 'user', texto: pergunta }]);
    setTexto('');
    setCarregando(true);
    try {
      const resp = await chatService.sendMessage(pergunta, historico);
      const { texto: limpo, botoes } = extrairBotoes(resp?.message);
      setMensagens((prev) => [...prev, { id: Date.now() + 1, sender: 'bot', texto: limpo || 'Não consegui montar uma resposta agora. Pode reformular?', botoes }]);
    } catch (e) {
      const msg = e?.response?.data?.message || 'Não consegui responder agora. Tente de novo em instantes.';
      setMensagens((prev) => [...prev, { id: Date.now() + 1, sender: 'bot', texto: msg, botoes: [] }]);
    } finally {
      setCarregando(false);
    }
  };

  const abrirTela = (caminho) => {
    navigate(caminho);
    if (celular) setAberto(false); // no celular o chat cobre a tela inteira
  };

  if (!aberto) {
    return (
      <Fab
        aria-label="Abrir assistente Aline"
        onClick={() => setAberto(true)}
        sx={{
          position: 'fixed', bottom: 24, right: 24, width: 64, height: 64, padding: 0, overflow: 'hidden',
          background: 'transparent', zIndex: 1250, boxShadow: '0 4px 20px rgba(102,126,234,0.5)',
          '&:hover': { background: 'transparent', boxShadow: '0 6px 24px rgba(102,126,234,0.7)' },
        }}
      >
        <img src={ALINE_FOTO} alt="Aline" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
      </Fab>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed', zIndex: 1300, display: 'flex', flexDirection: 'column', overflow: 'hidden',
        ...(celular
          ? { inset: 0, borderRadius: 0 }
          : { bottom: 24, right: 24, width: 390, height: 580, maxHeight: 'calc(100vh - 48px)', borderRadius: 3 }),
        backgroundColor: theme.palette.background.paper,
      }}
    >
      {/* Cabeçalho */}
      <Box sx={{ background: ROXO, color: 'white', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={ALINE_FOTO} alt="Aline" sx={{ width: 42, height: 42, border: '2px solid white' }} />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>Aline</Typography>
            <Typography variant="caption">Tira dúvidas sobre o sistema</Typography>
          </Box>
        </Box>
        <IconButton size="small" onClick={fechar} sx={{ color: 'white' }} aria-label="Fechar"><CloseIcon /></IconButton>
      </Box>

      {/* Conversa */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 2, bgcolor: theme.palette.background.default, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Avatar src={ALINE_FOTO} alt="Aline" sx={{ width: 30, height: 30 }} />
          <Paper elevation={1} sx={{ p: 1.5, maxWidth: '82%', borderRadius: 2, borderTopLeftRadius: 0 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {`Oi${primeiroNome ? `, ${primeiroNome}` : ''}! 😊 Eu sou a Aline. Posso te explicar como usar o Gerencie e mostrar onde fica cada funcionalidade. O que você quer saber?`}
            </Typography>
          </Paper>
        </Box>

        {mensagens.length === 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, pl: 5 }}>
            {sugestoes.map((s) => (
              <Chip key={s} label={s} size="small" clickable onClick={() => enviar(s)}
                sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' }, border: '1px solid', borderColor: 'divider' }} variant="outlined" />
            ))}
          </Box>
        )}

        {mensagens.map((m) => (
          <Box key={m.id} sx={{ display: 'flex', justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start', gap: 1, alignItems: 'flex-start' }}>
            {m.sender === 'bot' && <Avatar src={ALINE_FOTO} alt="Aline" sx={{ width: 30, height: 30 }} />}
            <Box sx={{ maxWidth: '82%' }}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5, borderRadius: 2,
                  borderTopLeftRadius: m.sender === 'bot' ? 0 : 16, borderTopRightRadius: m.sender === 'user' ? 0 : 16,
                  bgcolor: m.sender === 'user' ? theme.palette.primary.main : theme.palette.background.paper,
                  color: m.sender === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                }}
              >
                <TextoFormatado texto={m.texto} />
              </Paper>
              {m.botoes?.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
                  {m.botoes.map((b) => (
                    <Chip
                      key={b.caminho} label={b.rotulo} size="small" clickable color="primary"
                      icon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
                      onClick={() => abrirTela(b.caminho)} sx={{ fontWeight: 600 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {carregando && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Avatar src={ALINE_FOTO} alt="Aline" sx={{ width: 30, height: 30 }} />
            <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, borderTopLeftRadius: 0 }}><CircularProgress size={18} /></Paper>
          </Box>
        )}
        <div ref={fimRef} />
      </Box>

      {/* Digitar */}
      <Box sx={{ p: 1.5, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        <TextField
          fullWidth size="small" multiline maxRows={3} placeholder="Pergunte como usar o sistema..."
          value={texto} onChange={(e) => setTexto(e.target.value.slice(0, 800))}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); enviar(); } }}
          disabled={carregando}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
        />
        <IconButton
          onClick={() => enviar()} disabled={!texto.trim() || carregando} aria-label="Enviar"
          sx={{ background: ROXO, color: 'white', borderRadius: 2, '&.Mui-disabled': { bgcolor: '#ccc', color: '#999' } }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Paper>
  );
}
